import { Injectable } from '@angular/core';
import {
  Company,
  CompanyValidationStatus,
  User,
} from '@interfaces/auth.interface';

/**
 * Service responsible for managing application storage
 * Centralizes all localStorage operations to improve maintainability
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly STORAGE_KEYS = {
    TOKEN: 'token',
    COMPANY: 'company',
    AGENCY_COMPANY_ID: 'agency_company_id',
    COMPANY_DATA: 'company_data',
    EMAIL_VERIFIED: 'email_verified',
    COMPANY_STATUS: 'status_company',
    INVITATION_TOKEN: 'invitation_token',
    LANGUAGE: 'lang',
  };

  /**
   * Sets the authentication token
   * @param token The JWT token
   */
  setToken(token: string): void {
    localStorage.setItem(this.STORAGE_KEYS.TOKEN, token);
  }

  /**
   * Gets the authentication token
   * @returns The stored JWT token or null if not found
   */
  getToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.TOKEN);
  }

  /**
   * Sets the company ID
   * @param companyId The company ID
   */
  setCompanyId(companyId: string): void {
    localStorage.setItem(this.STORAGE_KEYS.COMPANY, companyId);
  }

  /**
   * Sets the company ID
   * @param agencyCompanyId The company ID
   */
  setAgencyCompanyId(agencyCompanyId: string): void {
    localStorage.setItem(this.STORAGE_KEYS.AGENCY_COMPANY_ID, agencyCompanyId);
  }

  /**
   * Gets the company ID
   */
  getAgencyCompanyId(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.AGENCY_COMPANY_ID);
  }

  /**
   * Gets the company ID
   * @returns The stored company ID or null if not found
   */
  getCompanyId(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.COMPANY);
  }

  /**
   * Sets the company data
   * @param company The company data object
   */
  setCompanyData(company: Company): void {
    localStorage.setItem(
      this.STORAGE_KEYS.COMPANY_DATA,
      JSON.stringify(company),
    );
  }

  /**
   * Gets the company data
   * @returns The stored company data or null if not found
   */
  getCompanyData(): Company | null {
    const data = localStorage.getItem(this.STORAGE_KEYS.COMPANY_DATA);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Sets the email verification status
   * @param verified Whether the email is verified
   */
  setEmailVerified(verified: boolean): void {
    localStorage.setItem(this.STORAGE_KEYS.EMAIL_VERIFIED, String(verified));
  }

  /**
   * Gets the email verification status
   * @returns Whether the email is verified or null if not found
   */
  getEmailVerified(): boolean | null {
    const value = localStorage.getItem(this.STORAGE_KEYS.EMAIL_VERIFIED);
    return value ? JSON.parse(value) : null;
  }

  /**
   * Sets the company validation status
   * @param status The company validation status
   */
  setCompanyStatus(status: CompanyValidationStatus): void {
    localStorage.setItem(this.STORAGE_KEYS.COMPANY_STATUS, String(status));
  }

  /**
   * Gets the company validation status
   * @returns The company validation status or null if not found
   */
  getCompanyStatus(): CompanyValidationStatus {
    return localStorage.getItem(
      this.STORAGE_KEYS.COMPANY_STATUS,
    ) as CompanyValidationStatus;
  }

  /**
   * Sets the invitation token
   * @param token The invitation token
   */
  setInvitationToken(token: string): void {
    localStorage.setItem(this.STORAGE_KEYS.INVITATION_TOKEN, token);
  }

  /**
   * Gets the invitation token
   * @returns The invitation token or null if not found
   */
  getInvitationToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.INVITATION_TOKEN);
  }

  /**
   * Removes the invitation token
   */
  removeInvitationToken(): void {
    localStorage.removeItem(this.STORAGE_KEYS.INVITATION_TOKEN);
  }

  /**
   * Sets the preferred language
   * @param lang The language code
   */
  setLanguage(lang: string): void {
    localStorage.setItem(this.STORAGE_KEYS.LANGUAGE, lang);
  }

  /**
   * Gets the preferred language
   * @returns The language code or null if not found
   */
  getLanguage(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.LANGUAGE);
  }

  /**
   * Clears all authentication and user data
   * Preserves language preference
   */
  clearAuthData(): void {
    const lang = this.getLanguage();

    localStorage.removeItem(this.STORAGE_KEYS.TOKEN);
    localStorage.removeItem(this.STORAGE_KEYS.COMPANY);
    localStorage.removeItem(this.STORAGE_KEYS.COMPANY_DATA);
    localStorage.removeItem(this.STORAGE_KEYS.EMAIL_VERIFIED);
    localStorage.removeItem(this.STORAGE_KEYS.AGENCY_COMPANY_ID);
    localStorage.removeItem(this.STORAGE_KEYS.COMPANY_STATUS);
    localStorage.removeItem(this.STORAGE_KEYS.INVITATION_TOKEN);

    if (lang) {
      this.setLanguage(lang);
    }
  }
}
